import { DiffEditor, type DiffOnMount } from "@monaco-editor/react";
import { useCallback } from "react";
import { CADPLM_THEME, THEME_NAME } from "../editor/monacoTheme";

interface DiffViewerProps {
  original: string;
  modified: string;
  originalLabel?: string;
  modifiedLabel?: string;
  onClose: () => void;
}

export default function DiffViewer({
  original,
  modified,
  originalLabel = "Original",
  modifiedLabel = "Modified",
  onClose,
}: DiffViewerProps) {
  const handleMount: DiffOnMount = useCallback((_editor, monaco) => {
    monaco.editor.defineTheme(THEME_NAME, CADPLM_THEME);
    monaco.editor.setTheme(THEME_NAME);
  }, []);

  return (
    <div className="diff-viewer">
      <div className="diff-viewer-header">
        <div className="diff-viewer-labels">
          <span className="diff-viewer-label diff-viewer-label--original">
            {originalLabel}
          </span>
          <span className="diff-viewer-separator">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </span>
          <span className="diff-viewer-label diff-viewer-label--modified">
            {modifiedLabel}
          </span>
        </div>
        <button
          className="diff-viewer-close"
          onClick={onClose}
          title="Close diff view"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      <div className="diff-viewer-body">
        <DiffEditor
          height="100%"
          language="javascript"
          theme="vs-dark"
          original={original}
          modified={modified}
          onMount={handleMount}
          options={{
            readOnly: true,
            originalEditable: false,
            minimap: { enabled: false },
            fontSize: 13,
            fontFamily: "'DM Mono', 'SF Mono', 'Fira Code', monospace",
            fontLigatures: true,
            lineNumbers: "on",
            scrollBeyondLastLine: false,
            automaticLayout: true,
            wordWrap: "on",
            renderLineHighlight: "line",
            smoothScrolling: true,
            padding: { top: 12, bottom: 12 },
            lineDecorationsWidth: 12,
            overviewRulerBorder: false,
            scrollbar: {
              verticalScrollbarSize: 6,
              horizontalScrollbarSize: 6,
              useShadows: false,
            },
            renderSideBySide: true,
            enableSplitViewResizing: true,
            renderOverviewRuler: false,
          }}
        />
      </div>
    </div>
  );
}
